// The living-signal connector (canon 91 §2a): the client half of the realtime
// "something changed" ping.
//
// WHY THE ISLAND OWNS THIS INSTEAD OF the htmx SSE extension. The extension
// (htmx-ext-sse) connects an EventSource declaratively from `sse-connect`, but
// on a connection error its handler calls htmx's `triggerErrorEvent`, and htmx
// core logs EVERY error event to `console.error` UNCONDITIONALLY (it is the
// first thing `he()` does when an event carries `error`, before any listener can
// intervene and with no config to gate it). So a `/signal` that is down — a
// worker/hub blip, a cold start, a proxy drop, which WILL happen in production —
// made the page throw a console error on a surface that is otherwise perfectly
// usable. The living signal is PURE PROGRESSIVE ENHANCEMENT: Postgres is the
// source of truth, and a missed ping is re-read on the next navigation (file 91
// §2a). A progressive-enhancement nudge must degrade SILENTLY; it must never
// surface an error. The extension cannot be made to, so the island owns the
// transport and the degradation instead — ~1 dependency dropped, full control.
//
// What this does: open ONE EventSource to /signal; on a message, re-dispatch a
// plain `sse:message` DOM event on #feed so the feed's declarative
// `hx-trigger="sse:message"` re-pulls the list (htmx still owns the swap — this
// file owns only the connection). On ANY error it stays SILENT: the browser's
// native EventSource auto-reconnect (per the SSE spec) heals a transient drop on
// its own, and a hub that never comes up simply never pings — the field is fully
// usable either way. Nothing here is on the critical path to a usable field.

/** A handle to the live-signal connection, so a caller can close it. */
export interface SignalHandle {
  close(): void;
}

/**
 * connectSignal opens the living-signal stream and routes each ping to the feed
 * as an `sse:message` event. It is resilient by construction: every failure mode
 * is swallowed, because the signal is an enhancement and its absence is normal.
 *
 * @param path   the SSE endpoint (default "/signal").
 * @param target the element the refresh trigger is dispatched on (default #feed).
 *               A missing target is fine — the event simply reaches nothing, the
 *               same no-op as a ping that arrives while a challenge is open.
 */
export function connectSignal(path = "/signal", targetId = "feed"): SignalHandle {
  // EventSource is ubiquitous, but guard anyway: if it is somehow unavailable,
  // the field still works — we just never wire the enhancement.
  if (typeof EventSource === "undefined") {
    return { close() {} };
  }

  let source: EventSource | null = null;
  let closed = false;

  try {
    source = new EventSource(path);
  } catch {
    // Construction itself can throw (a malformed URL, a blocked scheme). Degrade
    // silently — no signal, fully usable field.
    return { close() {} };
  }

  source.onmessage = () => {
    // A ping means "something changed, re-read". Re-dispatch as the plain DOM
    // event the feed listens for; htmx owns the actual GET + swap. The feed only
    // exists in feed state, so a ping while a challenge is open finds no #feed
    // and harmlessly does nothing — exactly the intended scoping.
    const feed = document.getElementById(targetId);
    if (feed) feed.dispatchEvent(new Event("sse:message", { bubbles: true }));
  };

  // The crux of the robustness fix: a connection error is NOT an application
  // error. Swallow it. The browser reconnects a dropped stream on its own; a
  // hub that is simply down leaves `source` in a retrying state and the field
  // carries on. We deliberately do NOT log, re-throw, or surface anything — a
  // progressive-enhancement nudge that is unavailable is a non-event.
  source.onerror = () => {
    // Intentionally empty. No console noise on a down/absent hub.
  };

  return {
    close() {
      if (closed) return;
      closed = true;
      source?.close();
    },
  };
}
